/* eslint-disable no-useless-catch */

import type { IFetchZelis, IGetPaymentsParams, IGetRow, IVoid } from '../../API/payments'
import type {
  IPaymentsRowItem,
  IStatusMap,
  IStatuses,
  LinePaymets,
  NotificationType
} from '../../interfaces'

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import Payments from '../../API/payments'
import { messageErrorFilterStatuses } from '../../models/const'

export interface IVoidParam extends IVoid {
  currentData: LinePaymets[]
}

export interface IGrid {
  data: LinePaymets[]
  dataRow: IPaymentsRowItem[]
  dataZelis: IFetchZelis[]
  isLoaded: boolean
  isLoadedClaim: boolean
  isLoadedZelis: boolean
  count: string
  countRow: string
  errorMessage: string
  statuses: IStatuses[]
  longTermStatuses: IStatuses[]
  statusMapping: IStatusMap
  avoidPending: boolean
  notification: {
    message: string
    status: NotificationType | null
  }
}

export const fetchInfoGrid = createAsyncThunk(
  'payment/fetchAllData',
  async (param: IGetPaymentsParams, { rejectWithValue }) => {
    const response = await Payments.getPayments(param).then(response => response)
    const date: LinePaymets[] = response.data.rows.map(item => {
      return { ...item, key: item.filenamesList }
    })
    response.data.rows = date
    return response.status === 200 ? response : rejectWithValue('Error')
  }
)

export const fetchAdditionalRow = createAsyncThunk(
  'payment/fetchRowByFileNames',
  async (params: IGetRow) => {
    try {
      const response = await Payments.getPaymentsRow(params).then(response => response)
      return response
    } catch (error) {
      throw error
    }
  }
)
export const fetchZelis = createAsyncThunk('payment/fetchZelis', async (params: IGetRow) => {
  try {
    const response = await Payments.getZelis(params).then(response => response)
    return response
  } catch (error) {
    throw error
  }
})

export const fetchStatuses = createAsyncThunk('paymentFilter/fetchStatus', async () => {
  try {
    const response = await Payments.getStatuses().then(response => response)
    return response.data
  } catch (error) {
    throw error
  }
})

export const avoid = createAsyncThunk('payment/avoid', async (params: IVoidParam) => {
  const response = await Payments.void({
    fileNames: params.fileNames,
    groupNumber: params.groupNumber,
    entId: params.entId
  }).then(response => response)
  let newData: LinePaymets[] = []
  newData = params.currentData.map(item => {
    if (item.filenamesList === params.fileNames && item.groupNumber === params.groupNumber) {
      return { ...item, status: String(response.newStatus) }
    }
    return item
  })
  return newData
})
export const rebuild = createAsyncThunk('payment/rebuild', async (params: IVoidParam) => {
  const response = await Payments.rebuild({
    fileNames: params.fileNames,
    groupNumber: params.groupNumber,
    entId: params.entId
  }).then(response => response)
  let newData: LinePaymets[] = []
  newData = params.currentData.map(item => {
    if (item.filenamesList === params.fileNames && item.groupNumber === params.groupNumber) {
      return { ...item, status: String(response.newStatus) }
    }
    return item
  })
  return newData
})

const initialState: IGrid = {
  data: [],
  dataZelis: [],
  dataRow: [],
  isLoaded: false,
  isLoadedClaim: false,
  isLoadedZelis: false,
  count: '0',
  errorMessage: '',
  countRow: '',
  statuses: [],
  longTermStatuses: [],
  statusMapping: {},
  avoidPending: false,
  notification: {
    message: '',
    status: null
  }
}

export const gridSlice = createSlice({
  name: 'grid',
  initialState,
  reducers: {
    clearExpandData: state => {
      state.dataRow = []
    },
    clearData: state => {
      state.data = []
    },
    changeData: (state, action) => {
      state.data = action.payload
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchInfoGrid.fulfilled, (state, action) => {
      state.data = action.payload.data.rows
      state.count = action.payload.data.count
      state.isLoaded = false
    })
    builder.addCase(fetchInfoGrid.pending, (state, _action) => {
      state.isLoaded = true
    })
    builder.addCase(fetchInfoGrid.rejected, (state, action) => {
      state.errorMessage = action.payload as string
    })
    builder.addCase(fetchAdditionalRow.fulfilled, (state, action) => {
      state.dataRow = action.payload.data
      state.countRow = action.payload.count
      state.isLoadedClaim = false
    })
    builder.addCase(fetchAdditionalRow.pending, (state, _action) => {
      state.isLoadedClaim = true
    })
    // builder.addCase(fetchAdditionalRow.pending, (state,action) => {
    //   state.isLoadedClaim = false
    // })
    builder.addCase(fetchStatuses.fulfilled, (state, action) => {
      state.statusMapping = action.payload.statusMapping
      state.statuses = action.payload.statuses
      state.longTermStatuses = action.payload.longTermStatuses
    })
    builder.addCase(fetchStatuses.rejected, (state, _action) => {
      state.notification = { status: 'error', message: messageErrorFilterStatuses }
    })
    builder.addCase(avoid.fulfilled, (state, action) => {
      state.data = action.payload
      state.avoidPending = false
    })
    builder.addCase(avoid.pending, (state, _action) => {
      state.avoidPending = true
    })
    builder.addCase(rebuild.fulfilled, (state, action) => {
      state.data = action.payload
      state.avoidPending = false
    })
    builder.addCase(rebuild.pending, (state, _action) => {
      state.avoidPending = true
    })
    builder.addCase(fetchZelis.fulfilled, (state, action) => {
      state.dataZelis = action.payload.data
      state.countRow = action.payload.count
      state.isLoadedZelis = false
    })
    builder.addCase(fetchZelis.pending, (state, _action) => {
      state.isLoadedZelis = true
    })
  }
})
export const { clearExpandData, clearData, changeData } = gridSlice.actions
export default gridSlice.reducer
